import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";

const KEY = "fs_longevity_state_v1";
const QuestionnaireContext = createContext(null);

const emptyState = {
  submissionId: null,
  resume: false,
  client: { first_name: "", last_name: "", email: "", phone: "", birth_date: "", gender: "" },
  consents: { privacy_accepted: false, consent_accepted: false },
  answers: {},
};

function loadState() {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { ...emptyState, ...JSON.parse(raw) };
  } catch (e) { /* noop */ }
  return { ...emptyState };
}

export const QuestionnaireProvider = ({ children }) => {
  const [state, setState] = useState(loadState);
  const saveTimer = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(state));
    } catch (e) { /* noop */ }
  }, [state]);

  const setClient = useCallback((client) => {
    setState((s) => ({ ...s, client: { ...s.client, ...client } }));
  }, []);

  const setConsents = useCallback((consents) => {
    setState((s) => ({ ...s, consents: { ...s.consents, ...consents } }));
  }, []);

  // Follow-up: resume a draft created by staff (its id comes from a shared link).
  const startResume = useCallback((id) => {
    setState({ ...emptyState, submissionId: id, resume: true });
  }, []);

  // Record consents on an already-existing draft (follow-up flow).
  const recordConsents = useCallback(async (id, consents) => {
    await api.post(`/submissions/${id}/consents`, {
      privacy_accepted: !!consents.privacy_accepted,
      consent_accepted: !!consents.consent_accepted,
    });
  }, []);

  // Create the draft submission on the server (idempotent per session) — baseline flow.
  const ensureSubmission = useCallback(async (client, consents) => {
    if (state.submissionId) {
      try { await api.patch(`/submissions/${state.submissionId}`, { client }); } catch (e) { /* noop */ }
      return state.submissionId;
    }
    const payload = {
      client,
      consents: {
        privacy_accepted: !!consents.privacy_accepted,
        consent_accepted: !!consents.consent_accepted,
      },
    };
    const { data } = await api.post("/submissions", payload);
    setState((s) => ({ ...s, submissionId: data.id }));
    return data.id;
  }, [state.submissionId]);

  // progressive server save (debounced)
  const scheduleSave = useCallback((id, answers) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      if (!id) return;
      api.patch(`/submissions/${id}`, { answers }).catch(() => { /* offline-safe: kept in localStorage */ });
    }, 600);
  }, []);

  const setAnswer = useCallback((qid, value) => {
    setState((s) => {
      const answers = { ...s.answers, [qid]: value };
      scheduleSave(s.submissionId, { [qid]: value });
      return { ...s, answers };
    });
  }, [scheduleSave]);

  const submit = useCallback(async () => {
    if (!state.submissionId) throw new Error("Nessun questionario da inviare");
    await api.patch(`/submissions/${state.submissionId}`, { answers: state.answers });
    await api.post(`/submissions/${state.submissionId}/submit`);
  }, [state.submissionId, state.answers]);

  const reset = useCallback(() => {
    try { localStorage.removeItem(KEY); } catch (e) { /* noop */ }
    setState({ ...emptyState });
  }, []);

  return (
    <QuestionnaireContext.Provider
      value={{ ...state, setClient, setConsents, ensureSubmission, startResume, recordConsents, setAnswer, submit, reset }}
    >
      {children}
    </QuestionnaireContext.Provider>
  );
};

export const useQuestionnaire = () => useContext(QuestionnaireContext);
